import { ReactNode, useState } from 'react';
import classNames from 'classnames';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import MedicationIcon from '@mui/icons-material/Medication';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GroupIcon from '@mui/icons-material/Group';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RedeemIcon from '@mui/icons-material/Redeem';
import ClinicalTrialInquiryModal from './ClinicalTrialInquiryModal';

interface TrialProtocol {
  title: string;
  subtitle: string;
  hospital: string;
  description: string;
  targetAudience: string[];
  participationContent: string[];
  benefits: string;
  sideEffects: string;
  contact: string;
  contactCenter: string;
  icon: ReactNode;
}

const trials: { id: number; name: string; protocol: TrialProtocol }[] = [
  {
    id: 1,
    name: '두통 (디지털 치료기기)',
    protocol: {
      title: '디지털 치료기기 임상시험 대상자 모집',
      subtitle: '만성 편두통 환자분을 모집합니다',
      hospital: '○○○병원',
      description:
        '○○○병원에서는 만성 편두통 환자에서 디지털 치료기기(앱 기반 치료 프로그램)의 효과와 안전성을 평가하기 위한 제3상 임상시험을 진행하고 있습니다.',
      targetAudience: [
        '만 18세 이상 성인',
        '만성 편두통 또는 반복적인 두통 진단을 받은 분',
        '최근 3개월 이상 두통 증상이 지속된 분',
        '스마트폰 사용이 가능한 분',
      ],
      participationContent: ['스마트폰 기반 치료 프로그램 사용', '참여 기간: 약 3~6개월', '정기 방문 시 진료 및 설문 평가 진행'],
      benefits:
        '본 임상시험에 참여하시는 경우 방문 일정에 따라 교통비 및 소정의 사례비가 지급될 수 있습니다. (지급 기준은 기관 승인 절차에 따릅니다.)',
      sideEffects: '사용 중 눈의 피로, 일시적인 두통 악화 등 불편감이 나타날 수 있으며 예상하지 못한 이상반응이 발생할 수도 있습니다.',
      contact: '02-XXX-XXXX',
      contactCenter: '○○○병원 신경과 임상시험센터',
      icon: <SmartphoneIcon className='!w-4 !h-4' />,
    },
  },
  {
    id: 2,
    name: '두통(신약)',
    protocol: {
      title: '신약 임상시험 대상자 모집',
      subtitle: '만성 두통 환자분을 모집합니다',
      hospital: '○○○병원',
      description:
        '○○○병원에서는 만성 두통 환자에서 새롭게 개발된 두통 치료 신약의 효과와 안전성을 평가하기 위한 제3상 무작위배정 임상시험을 진행하고 있습니다.',
      targetAudience: [
        '만 18세 이상 성인',
        '만성 두통 또는 반복적인 편두통 진단을 받은 분',
        '최근 3개월 이상 두통 증상이 지속된 분',
      ],
      participationContent: ['신약 또는 대조약 복용', '연구 기간: 약 8~12주', '방문 시 진료, 설문평가, 검사 시행'],
      benefits:
        '임상시험 참여로 인해 발생하는 방문 일정에 따라 교통비 및 소정의 사례비가 제공될 수 있습니다. (세부 내용은 상담 시 안내드립니다.)',
      sideEffects: '복용 중 오심, 불면, 졸림, 어지러움 등의 증상이 나타날 수 있으며 예상하지 못한 부작용이 발생할 수도 있습니다.',
      contact: '02-XXX-XXXX',
      contactCenter: '○○○병원 신경과 임상시험센터',
      icon: <MedicationIcon className='!w-4 !h-4' />,
    },
  },
];

interface CollapsibleSectionProps {
  title: string;
  icon: ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection = ({ title, icon, children, defaultOpen = false }: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className='border-b border-border last:border-b-0'>
      <button
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        className='flex items-center justify-between w-full py-3 text-left hover:bg-muted/50 transition-colors'
      >
        <div className='flex items-center gap-2'>
          <span className='text-primary'>{icon}</span>
          <span className='font-medium text-sm text-foreground'>{title}</span>
        </div>
        <ExpandMoreIcon className={classNames('!w-4 !h-4 text-muted-foreground transition-transform duration-200', isOpen && 'rotate-180')} />
      </button>
      {isOpen && <div className='pb-3 pl-6 pr-2 text-sm text-muted-foreground'>{children}</div>}
    </div>
  );
};

const ClinicalTrialAds = () => {
  const [expandedTrialId, setExpandedTrialId] = useState<number | null>(null);
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [selectedTrialName, setSelectedTrialName] = useState('');

  const handleTrialClick = (trialId: number) => {
    setExpandedTrialId(expandedTrialId === trialId ? null : trialId);
  };

  const handleInquiryClick = (trialName: string) => {
    setSelectedTrialName(trialName);
    setInquiryModalOpen(true);
  };

  return (
    <div className='space-y-3'>
      {trials.map((trial) => (
        <div key={trial.id} className='border-2 border-primary/30 rounded-lg bg-white overflow-hidden'>
          <button
            type='button'
            onClick={() => handleTrialClick(trial.id)}
            className='w-full p-4 cursor-pointer hover:bg-primary/5 transition-colors'
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <span className='text-primary'>{trial.protocol.icon}</span>
                <h4 className='text-base font-bold text-primary'>{trial.name}</h4>
              </div>
              <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                <span>더 알아보기</span>
                <ExpandMoreIcon
                  className={classNames('!w-4 !h-4 transition-transform duration-200', expandedTrialId === trial.id && 'rotate-180')}
                />
              </div>
            </div>
          </button>

          {expandedTrialId === trial.id && (
            <div className='px-4 pb-4 border-t border-primary/20'>
              <div className='py-4 space-y-2'>
                <h5 className='text-base font-bold text-foreground'>{trial.protocol.title}</h5>
                <p className='text-sm text-primary font-medium'>{trial.protocol.subtitle}</p>
                <p className='text-xs text-muted-foreground'>{trial.protocol.description}</p>

                <button
                  type='button'
                  onClick={() => handleInquiryClick(trial.name)}
                  style={{
                    color: 'white',
                  }}
                  className='w-full mt-3 py-3 px-4 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2'
                >
                  참여 신청하기
                </button>
              </div>

              <div className='bg-muted/30 rounded-lg'>
                <CollapsibleSection title='참여 대상' icon={<GroupIcon className='!w-4 !h-4' />} defaultOpen>
                  <ul className='list-disc list-inside space-y-1'>
                    {trial.protocol.targetAudience.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </CollapsibleSection>

                <CollapsibleSection title='연구 참여 내용' icon={<CalendarMonthIcon className='!w-4 !h-4' />}>
                  <ul className='list-disc list-inside space-y-1'>
                    {trial.protocol.participationContent.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </CollapsibleSection>

                <CollapsibleSection title='연구 참여에 따른 지원' icon={<RedeemIcon className='!w-4 !h-4' />}>
                  <p>{trial.protocol.benefits}</p>
                </CollapsibleSection>

                <CollapsibleSection title='부작용 가능성' icon={<WarningAmberIcon className='!w-4 !h-4' />}>
                  <p>{trial.protocol.sideEffects}</p>
                </CollapsibleSection>

                <CollapsibleSection title='문의처' icon={<PhoneIcon className='!w-4 !h-4' />}>
                  <div className='space-y-1'>
                    <p className='font-medium text-foreground'>☎ {trial.protocol.contact}</p>
                    <p>{trial.protocol.contactCenter}</p>
                  </div>
                </CollapsibleSection>
              </div>
            </div>
          )}
        </div>
      ))}

      <ClinicalTrialInquiryModal
        open={inquiryModalOpen}
        onOpenChange={setInquiryModalOpen}
        trialName={selectedTrialName}
      />
    </div>
  );
};

export default ClinicalTrialAds;
